import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ApiListResponse, ApiResponse } from '../api';
import { ApiSearch } from './api-utils.service';
import { ApiService } from './api.service';
import { Folder } from './folder';

@Injectable({
  providedIn: 'root'
})
export class FoldersApiService extends ApiService<Folder> {
  static readonly ROOT_FOLDER_ID = '00000000-0000-0000-0000-000000000000';
  static readonly FOLDER_WRITABLE_FIELDS = [
    'name', 'kind', 'parentId'
  ];
  static readonly SORT_FIELDS = [
    'name',
  ];

  constructor(
    protected httpClient: HttpClient,
    protected companyStateService: CompanyStateService
  ) {
    super(httpClient);
  }

  override getResourceUrl(): string {
    return '/v2/folders';
  }

  override toServerModel(entity: Folder, isNew: boolean): any {
    // Only send the writable fields to the server
    const fields: any = {};

    FoldersApiService.FOLDER_WRITABLE_FIELDS.forEach(field => {
      if (entity[field] != null) {
        fields[field] = entity[field];
      }
    });
    return fields;
  }

  list(search: ApiSearch, cursor?: string): Promise<ApiListResponse<Folder>> {
    const params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('search', search.query || '*')
      .set('sort', this.getSortString(search))
      .set('cursor', cursor || '')
      .set('includeSubcompanies', search.includeSubcompanies || false);

    return firstValueFrom(super.listAsObservable(params));
  }

  private getSortString(search: ApiSearch): string {
    let sortString = 'name';
    if (FoldersApiService.SORT_FIELDS.includes(search.sortBy)) {
      sortString = search.sortBy + (search.reverse ? ' desc' : ' asc');
    } else {
      sortString += ' asc';
    }
    return sortString;
  }

  get(folderId: string): Promise<ApiResponse<Folder>> {
    return firstValueFrom(super.getAsObservable(folderId));
  }

  add(folder: Folder): Promise<ApiResponse<Folder>> {
    return firstValueFrom(super.addAsObservable(this.companyStateService.getSelectedCompanyId(), folder));
  }

  update(folderId: string, folder: Folder): Promise<ApiResponse<Folder>> {
    return firstValueFrom(super.updateAsObservable(folderId, folder));
  }

  delete(folderId: string): Promise<ApiResponse<Folder>> {
    return firstValueFrom(super.deleteAsObservable(folderId));
  }

  get rootFolderId(): string {
    return FoldersApiService.ROOT_FOLDER_ID;
  }
}
